import { Container, Grid, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ProductDetailView.styles";
import { withStyles } from "@material-ui/core/styles";
import React, { useEffect } from "react";
import { Breadcrumbs } from "../components/Breadcrumbs/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { DATE_FORMAT } from "../../constants/TIME";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import {
    convertDateStringToNumber,
    getEndDate,
    getStartDate,
    isFromToValid,
    mapNumberToDate,
    numberToDateFormat
} from "../../utilities/dateTimeUtils";
import * as BasketAction from "../../stores/basket/BasketAction";
import { addItem } from "../../stores/basket/BasketAction";
import { getDefaultVat } from "../../utilities/countriesUtils";
import { requestFilesInfo } from "../../stores/filesInfo/FilesInfoAction";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useAuthContext } from "../../auth/AuthProvider";
import { SampleFiles } from "./productDetails/SampleFiles";
import { useHistory, useParams } from "react-router-dom";
import { ProductDetailsCheckoutContent } from "./productDetails/ProductDetailsCheckoutContent";
import { LOGOS } from "./productOverview/VenueConfig";
import {
    BUYING_OPTION_OTP,
    BUYING_OPTION_SUBSCRIPTION,
    findProductByName,
    hasVenue,
    isProductProhibitedForUser
} from "./productOverview/ProductHelper";
import { requestProductFiles } from "../../stores/productFiles/ProductFileAction";
import * as SubscriptionAction from "../../stores/subscriptions/SubscriptionAction";
import SubscriptionModel from "../../models/subscription/SubscriptionModel";
import { requestProducts, requestProductSubscriptionTypes } from "../../stores/products/ProductsAction";

import Alert from "@material-ui/lab/Alert/Alert";
import { OtherProducts } from "../components/OtherProducts/OtherProducts";
import { ISubscriptionType } from "./productDetails/kaiko/KaikoProductDetailsSubscriptionBox";
import { getSubscriptionBoxType } from "./productDetails/helpers/subscriptionBoxTypesHelper";
import { useModulePricing } from "../../hooks/useModulePricing";
import { selectVisibleCountries } from "../../selectors/countries/CountriesSelector";

export interface IProductDetailViewProps extends WithStyles<typeof styles> {}

function toSubscriptionIds(userSubscriptionsFromState) {
    return userSubscriptionsFromState
        ? Object.values(userSubscriptionsFromState)?.map((e: SubscriptionModel) => e.typeId)
        : [];
}

const getSubscriptionTypesForProduct = (subscriptionTypesFromState): ISubscriptionType[] => {
    return subscriptionTypesFromState
        ? Object.values(subscriptionTypesFromState)?.map((s: SubscriptionModel) => ({ id: s.id, name: s.description }))
        : [];
};

const ProductDetailViewPure: React.FunctionComponent<IProductDetailViewProps> = ({
    classes
}: IProductDetailViewProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { productIdentifier } = useParams();
    const history = useHistory();
    const authContext = useAuthContext();
    const {useHasProductModulePricing} = useModulePricing();

    const userId = authContext?.user?.id;

    const countries = useSelector(state => selectVisibleCountries(state));

    const filesInfo = useSelector(state => state.filesInfo);
    const productFiles = useSelector(state => Object.values(state.productFiles));

    const products = useSelector(state => state.products?.products);
    const categories = useSelector(state => state.products?.categories);
    const thisProduct = findProductByName(products, productIdentifier);
    const otherProducts = thisProduct?.upsellingProducts
        ?.map(p => findProductByName(products, p))
        .filter(p => p !== undefined);

    const otpEnabled = thisProduct?.order?.buyingOptions.includes(BUYING_OPTION_OTP);
    const subscriptionEnabled = thisProduct?.order?.buyingOptions.includes(BUYING_OPTION_SUBSCRIPTION);

    const userSubscriptionsFromState = useSelector(state => state.subscriptions?.userSubscriptions);
    const productSubscriptionTypesfromState = useSelector(state => state.products?.subscriptionTypesForCurrentProduct);
    const subscriptionIds = toSubscriptionIds(userSubscriptionsFromState);
    const productSubscriptionTypes = getSubscriptionTypesForProduct(productSubscriptionTypesfromState);
    const firstAvailableDate = thisProduct?.order?.firstAvailableDate;
    const lastAvailableDate = thisProduct?.order?.lastAvailableDate;
    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);

    const basketState = useSelector(state => state.basket?.state);


    const hasThisProductModulePricing = useHasProductModulePricing(thisProduct?.id);

    const [state, setState] = React.useState({
        // state for default products
        from: isIigf() ? numberToDateFormat(firstAvailableDate) : getStartDate(lastAvailableDate),
        to: getEndDate(lastAvailableDate)
    });

    function isIigf() {
        return hasVenue(thisProduct?.venue, "IIGF");
    }

    useEffect(() => {
        dispatch(requestProducts());
        dispatch(requestProductFiles(productIdentifier));
        dispatch(requestProductSubscriptionTypes(productIdentifier));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (lastAvailableDate && firstAvailableDate) {
            setState({
                ...state,
                from: isIigf() ? numberToDateFormat(firstAvailableDate) : getStartDate(lastAvailableDate),
                to: getEndDate(lastAvailableDate)
            });
        }
        // eslint-disable-next-line
    }, [firstAvailableDate, lastAvailableDate]);

    useEffect(() => {
        dispatch(BasketAction.requestBasket(userId));

        if (userId) {
            dispatch(SubscriptionAction.requestUserSubscriptions(userId));
        }
        if (otpEnabled && isFromToValid(state.from, state.to)) {
            dispatch(
                requestFilesInfo(
                    thisProduct?.id,
                    convertDateStringToNumber(state.from),
                    convertDateStringToNumber(state.to)
                )
            );
        }
    }, [
        dispatch,
        userId,
        otpEnabled,
        productIdentifier,
        state.from,
        state.to,
        thisProduct?.id,
        thisProduct?.isFilterableProduct
    ]);

    const prices = {
        historical: {
            startingAt: filesInfo.dailyPrice,
            minimalDays: filesInfo.minimalDays,
            firstAvailableDate: mapNumberToDate(firstAvailableDate).format(DATE_FORMAT),
            lastAvailableDate: mapNumberToDate(lastAvailableDate).format(DATE_FORMAT),
            files: filesInfo.numberOfFiles,
            total: filesInfo.price,
            numberOfModules: filesInfo.numberOfModules,
            currency: "€"
        }
    };

    const handleCheckoutHistorical = (from: string, to: string, price: number, additionalParams: any) => {
        dispatch(BasketAction.requestBasket(userId));
        if (basketState === "Locked") {
            enqueueSnackbar("Can not add to basket during the payment process!", { variant: ToastStatusEnum.Error });
        } else {
            const fromNumber = convertDateStringToNumber(from || state.from);
            const toNumber = convertDateStringToNumber(to || state.to);
            const newOrderItem = {
                product: thisProduct?.id,
                from: fromNumber,
                to: toNumber,
                price: price || filesInfo.price,
                ...(hasThisProductModulePricing && {
                    numberOfModules: filesInfo.numberOfModules
                }),
                productRemark: filesInfo.remark,
                // Additional parameters - for Kaiko
                ...(additionalParams && additionalParams)
            };

            dispatch(addItem(userId, newOrderItem, getDefaultVat(countries)))
                .then(() => {
                    dispatch(BasketAction.requestBasket(userId));
                    enqueueSnackbar(t("dataView.addedToCart"), { variant: ToastStatusEnum.Info });
                })
                .catch(e => {
                    enqueueSnackbar(e.message, { variant: ToastStatusEnum.Error });
                });
        }
    };

    const handleCheckoutSubscription = () => {
        const subscriptionBoxType = getSubscriptionBoxType({
            isSubscribed: isSubscribed(),
            productId: thisProduct?.id
        });

        history.push({
            pathname: subscriptionBoxType?.pathname,
            ...(subscriptionBoxType?.getState && {
                state: subscriptionBoxType?.getState({ categories, product: thisProduct })
            })
        });
    };

    const handleDateChange = (from, to) => {
        setState({
            ...state,
            from: from,
            to: to
        });

        if (isFromToValid(from, to)) {
            dispatch(requestFilesInfo(thisProduct.id, convertDateStringToNumber(from), convertDateStringToNumber(to)));
        }
    };

    function isSubscribed() {
        return productSubscriptionTypes ? productSubscriptionTypes.some(sT => subscriptionIds.includes(sT.id)) : false;
    }

    return thisProduct ? (
        <Container>
            <Breadcrumbs
                items={[{ title: t("mainNavBar.productView"), href: "/products" }, { title: thisProduct?.name }]}
            />
            <Grid container spacing={6} className={classes.productDetails}>
                <Grid item xs={5}>
                    <div className={classes.productExplanation}>
                        {thisProduct &&
                            thisProduct.venue &&
                            thisProduct.venue.map(
                                venue => venue in LOGOS && <img key={venue} src={"../../" + LOGOS[venue]} alt={venue} />
                            )}
                        <Typography variant="h2">{thisProduct?.name}</Typography>
                        <Typography variant="body1">
                            <span dangerouslySetInnerHTML={{ __html: thisProduct?.description }} />
                        </Typography>

                        <div>
                            <SampleFiles files={productFiles} useCaseCategoryId={thisProduct.useCaseCategoryId} />
                        </div>
                    </div>
                </Grid>
                <Grid item xs={7}>
                    {!isProductProhibitedForUser(thisProduct, userDetails) ? (
                        <ProductDetailsCheckoutContent
                            headlineDisabled={false}
                            from={state.from}
                            to={state.to}
                            rowId={thisProduct.id}
                            prices={prices}
                            remark={filesInfo.remark}
                            handleCheckoutSubscription={handleCheckoutSubscription}
                            handleCheckoutHistorical={handleCheckoutHistorical}
                            handleDateChange={handleDateChange}
                            otpEnabled={otpEnabled}
                            subscriptionEnabled={subscriptionEnabled}
                            isAlreadySubscribed={isSubscribed()}
                            hideInterval={isIigf()}
                            showBusinessDays={!isIigf()}
                        />
                    ) : (
                        <Alert severity="warning" variant="outlined">
                            This product is not available in your region.
                            <br />
                            If you need further information, please contact hdp@deutsche-boerse.com.
                        </Alert>
                    )}
                </Grid>
                <OtherProducts currentProductName={thisProduct.name} otherProducts={otherProducts} />
            </Grid>
        </Container>
    ) : null;
};

export const ProductDetailView = withStyles(styles)(ProductDetailViewPure);
